import jwtDecode from 'jwt-decode';

import { StorageService, StorageKeys } from '@packages/core/storage';
import { AuthTokenPayload, createTokenReceipt, secondsUntilExpiry, TokenReceipt } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { Account } from '@packages/models/api';

const logger = getLogger('auth');

export interface StoredToken {
    token: string | null;
    decodedToken: AuthTokenPayload | null;
    /**
     * Account profile parsed from the token's `account` claim, or `null` when the
     * claim is absent/unparseable. Callers fall back to the accounts API in that case.
     */
    account: Account | null;
}

export class AuthTokenService {
    constructor(private storageService: StorageService) {}

    private decodeAndValidate(token: string, receipt: TokenReceipt | null = null): AuthTokenPayload | null {
        let payload: AuthTokenPayload;

        try {
            payload = jwtDecode(token);
        } catch (e) {
            logger.error('Failed to decode the access token', { error: e });
            return null;
        }

        if (secondsUntilExpiry(payload, receipt) <= 0) {
            return null;
        }

        return payload;
    }

    private parseAccountClaim(payload: AuthTokenPayload | null): Account | null {
        if (!payload?.account) {
            return null;
        }

        try {
            return JSON.parse(payload.account) as Account;
        } catch (e) {
            logger.error('Failed to parse the account claim from the access token', { error: e });
            return null;
        }
    }

    /**
     * Extracts the account profile from a raw token string. Used when only the
     * token is available (e.g. the post-refresh token update) rather than a
     * previously resolved {@link StoredToken}.
     */
    getAccountFromToken(token: string): Account | null {
        return this.parseAccountClaim(this.decodeAndValidate(token));
    }

    async getStoredToken() {
        let storedToken: StoredToken = { token: null, decodedToken: null, account: null };

        try {
            storedToken.token = await this.storageService.getItem(StorageKeys.AccessToken);
        } catch (e) {
            logger.error('Failed to read the stored access token', { error: e });
            return storedToken;
        }

        if (!storedToken.token) {
            return storedToken;
        }

        let receipt: TokenReceipt | null = null;
        try {
            receipt = await this.storageService.getItem(StorageKeys.AccessTokenReceivedAt);
        } catch (e) {
            logger.warn('Failed to read when the access token was received', { error: e });
        }

        storedToken.decodedToken = this.decodeAndValidate(storedToken.token, receipt);
        storedToken.account = this.parseAccountClaim(storedToken.decodedToken);

        return storedToken;
    }

    async storeToken(token: string): Promise<StoredToken | null> {
        const decodedToken = this.decodeAndValidate(token);

        if (!decodedToken) {
            return null;
        }

        try {
            const receipt = createTokenReceipt(decodedToken);

            if (receipt) {
                await this.storageService.setItem(StorageKeys.AccessTokenReceivedAt, receipt);
            } else {
                await this.storageService.removeItem(StorageKeys.AccessTokenReceivedAt);
            }
            await this.storageService.setItem(StorageKeys.AccessToken, token);
        } catch (e) {
            logger.error('Failed to save the access token to storage', { error: e });
            return null;
        }

        return { token, decodedToken, account: this.parseAccountClaim(decodedToken) };
    }
}
